import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Copy, Megaphone, Info, Cake } from "lucide-react";
import { api, API, imageUrl, fullDate } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white outline-none focus:border-[#D4AF37] text-sm";

export default function Campaigns() {
  const [audience, setAudience] = useState(null);
  const [birthdays, setBirthdays] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  useEffect(() => {
    api.get("/admin/campaigns/audience").then((r) => setAudience(r.data)).catch(() => {});
    api.get("/admin/campaigns/birthdays").then((r) => setBirthdays(r.data)).catch(() => {});
    api.get("/admin/events").then((r) => setEvents(r.data.filter((e) => !["draft", "hidden"].includes(e.status)))).catch(() => {});
  }, []);

  const selectedEvent = events.find((e) => e.id === eventId);

  const applyEvent = (id) => {
    setEventId(id);
    const e = events.find((x) => x.id === id);
    if (e) {
      setSubject(`🎟️ ${e.name} — Tickets Now Available`);
      setBody(`We just dropped a new event you won't want to miss.\n\n${e.name}\n${fullDate(e.date)}${e.start_time ? ` · ${e.start_time}` : ""}\n${e.venue ? e.venue.name + " — " + e.venue.city : "Huntsville, AL"}\n\n${e.description || ""}\n\nGrab your tickets before they're gone.`);
    }
  };

  const origin = window.location.origin;
  const eventLink = selectedEvent ? `${origin}/events/${selectedEvent.slug}` : `${origin}/events`;

  const htmlPreview = `<h1>${subject}</h1>\n<p>${body.replace(/\n/g, "<br>")}</p>\n<p><a href="${eventLink}">Get Tickets</a></p>`;

  const copyHtml = () => { navigator.clipboard.writeText(htmlPreview); toast.success("Email HTML copied"); };
  const copyText = () => { navigator.clipboard.writeText(`${subject}\n\n${body}\n\nGet tickets: ${eventLink}`); toast.success("Text copied"); };
  const exportAudience = (consent) => window.open(`${API}/admin/campaigns/audience/export?consent=${consent}`, "_blank");

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl uppercase text-white mb-2 flex items-center gap-2"><Megaphone size={26} className="text-[#D4AF37]" /> Campaigns</h1>

      <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 mb-6 flex gap-2">
        <Info size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
        <p className="text-xs text-zinc-300">Compose your announcement here, then <strong className="text-white">export your opted-in audience</strong> and send it from a dedicated email marketing service (Mailchimp, Sender, Brevo, etc.). Bulk marketing is intentionally kept out of the transactional email pipeline used for ticket receipts.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 text-center"><p className="text-2xl font-bold text-white">{audience?.email_optin ?? "—"}</p><p className="text-[10px] text-zinc-500 uppercase mt-1">Email Opt-in</p></div>
        <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 text-center"><p className="text-2xl font-bold text-white">{audience?.sms_optin ?? "—"}</p><p className="text-[10px] text-zinc-500 uppercase mt-1">SMS Opt-in</p></div>
        <button data-testid="export-email-audience" onClick={() => exportAudience("email")} className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 flex flex-col items-center justify-center gap-1 hover:border-[#D4AF37]"><Download size={18} className="text-[#D4AF37]" /><span className="text-[10px] text-zinc-300 uppercase">Export Email</span></button>
        <button data-testid="export-sms-audience" onClick={() => exportAudience("sms")} className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 flex flex-col items-center justify-center gap-1 hover:border-[#D4AF37]"><Download size={18} className="text-[#D4AF37]" /><span className="text-[10px] text-zinc-300 uppercase">Export SMS</span></button>
      </div>

      <div className="rounded-xl border border-[#D4AF37]/40 bg-[#18181B] p-4 mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Cake size={22} className="text-[#D4AF37]" />
          <div>
            <p className="text-white font-semibold">{birthdays?.count ?? "—"} birthdays in {birthdays ? MONTHS[birthdays.month - 1] : "this month"}</p>
            <p className="text-xs text-zinc-400">Opted-in customers celebrating this month — send them a birthday-night offer.</p>
          </div>
        </div>
        <button data-testid="export-birthdays" onClick={() => window.open(`${API}/admin/campaigns/birthdays/export`, "_blank")} className="shrink-0 flex items-center gap-1.5 rounded-md bg-[#D4AF37] text-black px-3 py-2 text-xs font-oswald tracking-widest"><Download size={14} /> EXPORT</button>
      </div>

      <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 mb-4">
        <h2 className="font-oswald text-sm tracking-widest text-zinc-300 uppercase mb-3">Compose Announcement</h2>
        <label className="text-[10px] text-zinc-500">Auto-fill from an event (optional)</label>
        <Select value={eventId} onValueChange={applyEvent}>
          <SelectTrigger data-testid="campaign-event" className={field}><SelectValue placeholder="Choose an event" /></SelectTrigger>
          <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        <input data-testid="campaign-subject" placeholder="Subject line" className={`${field} mt-3`} value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea data-testid="campaign-body" rows={8} placeholder="Message…" className={`${field} mt-3`} value={body} onChange={(e) => setBody(e.target.value)} />
        <div className="flex gap-2 mt-3">
          <button data-testid="copy-html" onClick={copyHtml} className="flex items-center gap-1.5 rounded-md border border-[#27272A] px-3 py-2 text-xs text-white hover:border-[#D4AF37]"><Copy size={14} /> Copy HTML</button>
          <button data-testid="copy-text" onClick={copyText} className="flex items-center gap-1.5 rounded-md border border-[#27272A] px-3 py-2 text-xs text-white hover:border-[#D4AF37]"><Copy size={14} /> Copy Text</button>
        </div>
      </div>

      {(subject || body) && (
        <div className="rounded-xl border border-[#27272A] bg-white text-black overflow-hidden">
          <div className="bg-black px-4 py-3"><span className="text-[#D4AF37] font-display text-lg">ABE</span></div>
          <div className="p-5">
            {selectedEvent && <img src={imageUrl(selectedEvent.flyer_image)} alt="" className="w-full h-40 object-cover rounded-lg mb-4" />}
            <h3 className="font-bold text-lg">{subject}</h3>
            <p className="text-sm text-zinc-700 mt-2 whitespace-pre-line">{body}</p>
            <a href={eventLink} className="inline-block mt-4 bg-[#D4AF37] text-black px-5 py-2.5 rounded-md text-sm font-bold">Get Tickets</a>
          </div>
        </div>
      )}
    </div>
  );
}
